import { like } from '@/api/helpers';
import { FiltersType, ListingRequest, ListingResponse } from '@/api/types';
import { ITEMS_PER_LAZY_REQUEST, partialRequests } from '@/utils/helpers';
import { debounce } from 'lodash';
import { InputText } from 'primereact/inputtext';
import {
  MultiSelect,
  MultiSelectAllEvent,
  MultiSelectChangeEvent,
  MultiSelectProps,
} from 'primereact/multiselect';
import { Skeleton } from 'primereact/skeleton';
import { VirtualScrollerLazyEvent } from 'primereact/virtualscroller';
import React, {
  HTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

type LazyLoadingMultiselectProps = {
  fetchService?: (params: ListingRequest) => Promise<ListingResponse<any>>;
  handleOnChange: (values: any[]) => void;
  selectedValues: any[];
  additionalFilters?: FiltersType;
  optionFilter?: (value: string) => FiltersType;
  refetchKey?: number;
  isDisabled?: boolean;
  maxSelectedItems?: number;
  filterTemplateAddition?: JSX.Element;
  label?: string;
  isSearchable?: boolean;
} & MultiSelectProps &
  HTMLAttributes<HTMLSelectElement>;

type fetchDataParams = {
  filters: FiltersType;
  skipValue: number;
  append?: boolean;
  preserveSelected?: boolean;
};

export const LazyLoadingMultiselect: React.FC<LazyLoadingMultiselectProps> = ({
  fetchService,
  handleOnChange,
  selectedValues = [],
  additionalFilters,
  optionFilter = (value) => [like('name', value)],
  refetchKey = 0,
  isDisabled,
  maxSelectedItems,
  filterTemplateAddition,
  isSearchable = false,
  ...rest
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [skip, setSkip] = useState(0);
  const [count, setCount] = useState(0);
  const [filter, setFilter] = useState<FiltersType>([]);
  const [filterValue, setFilterValue] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  const currentFilters = useMemo(
    () => [...(additionalFilters ?? []), ...filter],
    [filter, additionalFilters],
  );
  const hasMore = skip < count;

  const fetchData = useCallback(
    async ({
      filters,
      skipValue,
      append = false,
      preserveSelected = false,
    }: fetchDataParams) => {
      if (!fetchService) return;

      setIsLoading(true);

      const response = await fetchService({
        filters,
        skip: skipValue,
        take: ITEMS_PER_LAZY_REQUEST,
        ...(isSearchable ? { search: filterValue } : {}),
      });

      const newData = response?.result ?? [];
      const count = response?.count ?? 0;

      setCount(count);

      if (append) {
        setData((prev) => {
          const uniqueNewData = newData.filter(
            (item) => !prev.some((existing) => existing.id === item.id),
          );
          return [...prev, ...uniqueNewData];
        });
      } else {
        if (preserveSelected) {
          const searchText = filterValue.trim().toLowerCase();
          const selectedNotInResults = selectedValues.filter(
            (selected) =>
              selected?.id &&
              !newData.some((item) => item.id === selected.id) &&
              (!searchText ||
                (selected.name &&
                  selected.name.toLowerCase().includes(searchText))),
          );
          setData([...newData, ...selectedNotInResults]);
        } else {
          setData(newData);
        }
      }

      setSkip(skipValue + ITEMS_PER_LAZY_REQUEST);

      setIsLoading(false);
    },
    [fetchService, filterValue],
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((searchText: string) => {
        const filters =
          !isSearchable && searchText.trim() ? optionFilter(searchText) : [];

        setFilter(filters);
        setSkip(0);
        const isFiltering = searchText.trim() !== '';

        fetchData({
          filters: [...(additionalFilters ?? []), ...filters],
          skipValue: 0,
          append: false,
          preserveSelected: isFiltering,
        }).finally(() => {
          setIsSearching(false);
        });
      }, 500),
    [fetchData, isSearchable, optionFilter, additionalFilters],
  );

  const handleSearchChange = (value: string) => {
    setFilterValue(value);
    setIsSearching(true);
    debouncedSearch(value);
  };

  const onLazyLoad = (event: VirtualScrollerLazyEvent) => {
    if (!isLoading && hasMore && Number(event.last) >= data.length) {
      fetchData({
        filters: currentFilters,
        skipValue: skip,
        append: true,
        preserveSelected: false,
      });
    }
  };

  const handleSelectAll = async (shouldSelectAll: boolean) => {
    if (!fetchService || isLoading) return;

    if (shouldSelectAll) {
      setIsLoading(true);
      const isFiltering = filterValue.trim() !== '';
      let allItems;

      if (isFiltering) {
        allItems = await partialRequests(currentFilters, fetchService);
      } else {
        allItems = await partialRequests(additionalFilters ?? [], fetchService);
      }

      setData(allItems);
      handleOnChange(allItems);
      setIsLoading(false);
    } else {
      handleOnChange([]);
    }
  };

  useEffect(() => {
    if (isSearching) {
      return;
    }

    const isCurrentlyFiltering = filterValue.trim() !== '';

    fetchData({
      filters: currentFilters,
      skipValue: 0,
      append: false,
      preserveSelected:
        isCurrentlyFiltering && filter.length > 0 ? true : false,
    });
  }, [refetchKey, filterValue, currentFilters, isSearching]);

  const virtualScrollerOptions = useMemo(() => {
    if (data.length > 8 && hasMore) {
      return {
        itemSize: 50,
        lazy: true,
        onLazyLoad,
        loadingTemplate: () => (
          <div className="flex align-items-center p-2">
            <Skeleton height="32px" />
          </div>
        ),
        showLoader: true,
        loading: isLoading,
        step: ITEMS_PER_LAZY_REQUEST,
        autoSize: true,
      };
    }
    return undefined;
  }, [data.length, isLoading, hasMore, skip]);

  const filterTemplate = (
    <div className="p-multiselect-filter-container w-full">
      <span className="p-input-icon-right w-full">
        <i className="pi pi-search" />
        <InputText
          className="p-multiselect-filter p-inputtext p-component"
          placeholder={rest.placeholder}
          autoFocus
          value={filterValue}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
      </span>
      {filterTemplateAddition}
    </div>
  );

  const isAllSelected = useMemo(() => {
    if (count === 0) return false;

    const isFiltering = filterValue.trim() !== '';

    if (isFiltering) {
      const availableFilteredItems = data.filter((option) => {
        const searchText = filterValue.trim().toLowerCase();
        return option.name && option.name.toLowerCase().includes(searchText);
      });

      const selectedFilteredCount = selectedValues.filter((selected) =>
        availableFilteredItems.some(
          (available) => available.id === selected.id,
        ),
      ).length;

      return (
        selectedFilteredCount === availableFilteredItems.length &&
        availableFilteredItems.length > 0
      );
    } else {
      return selectedValues.length === count;
    }
  }, [selectedValues, count, filterValue, data]);

  const onChipRemove = (item: any) => {
    const key = rest.dataKey || 'id';
    const newValue = selectedValues.filter((c: any) => c[key] !== item[key]);
    handleOnChange(newValue);
  };

  const selectedItemTemplate = (item: any) => {
    if (!item) {
      return null;
    }

    const labelValue = rest.optionLabel
      ? item?.[rest.optionLabel as string]
      : item?.name;

    return (
      <div className="p-multiselect-token" key={item?.id}>
        <div className="p-multiselect-token-label">{labelValue}</div>
        <button
          type="button"
          aria-label={t('generic.remove')}
          className="p-multiselect-token-icon pi pi-times"
          onClick={(e) => {
            e.stopPropagation();
            onChipRemove(item);
          }}
        />
      </div>
    );
  };

  return (
    <MultiSelect
      dataKey="id"
      filter={false}
      value={selectedValues}
      dropdownIcon={isLoading ? 'pi pi-spinner pi-spin' : 'pi pi-chevron-down'}
      selectedItemsLabel={t('generic.itemsSelected.count')}
      selectedItemTemplate={selectedItemTemplate}
      options={data}
      disabled={isDisabled}
      virtualScrollerOptions={virtualScrollerOptions}
      filterTemplate={filterTemplate}
      emptyMessage={t('filter.noItems')}
      onChange={(e: MultiSelectChangeEvent) => handleOnChange(e.value)}
      onSelectAll={(e: MultiSelectAllEvent) => handleSelectAll(!e.checked)}
      selectAll={isAllSelected}
      maxSelectedLabels={maxSelectedItems}
      display={
        !maxSelectedItems ||
        (maxSelectedItems && selectedValues.length <= maxSelectedItems)
          ? 'chip'
          : 'comma'
      }
      {...(rest as MultiSelectProps)}
    />
  );
};
